import React, { useMemo, useState } from 'react';
import DataTable from './DataTable';
import { describeFilter } from './ColumnFilter';
import ColumnPicker from './ColumnPicker';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import { Field, TextInput, Select } from '../ui/Field';
import './list.css';

/**
 * Applied column filters, as removable chips. Without these an admin has to reopen
 * each header menu to find out why a list looks short -- the funnel icon says a
 * column is filtered but not what by.
 */
export function ActiveFilterChips({
  columns,
  columnFilters,
  filterLabels = {},
  onRemove,
  onClearAll,
}) {
  const entries = Object.entries(columnFilters || {});
  if (entries.length === 0) return null;

  return (
    <div className="sv-chips">
      {entries.map(([key, filter]) => {
        const col = columns.find((c) => (c.filterKey || c.key) === key);
        // Some filters have no column of their own -- the trend chart's drill-down
        // filters on a day that no column displays.
        const virtual = filterLabels[key];
        const label = virtual?.label || col?.header || key;
        const kind = virtual?.kind || col?.filter;
        return (
          <span className="sv-chip" key={key}>
            <span className="sv-chip__label">{label}</span>
            <span className="sv-chip__value">{describeFilter(kind, filter)}</span>
            <button
              type="button"
              className="sv-chip__x"
              aria-label={`Remove filter on ${label}`}
              onClick={() => onRemove(key, null)}
            >
              <i className="bx bx-x" aria-hidden="true" />
            </button>
          </span>
        );
      })}
      {/* With one chip its own × is enough; past that a single control saves clicks. */}
      {entries.length > 1 && (
        <button type="button" className="sv-chips__clear" onClick={onClearAll}>
          Clear all
        </button>
      )}
    </div>
  );
}

/** Saved-view tabs with count pills. An attention view keeps its crimson pill when inactive. */
export function SavedViewTabs({ views, activeView, onChange, counts = {}, onSaveView }) {
  return (
    <div className="sv-views">
      {views.map((view, index) => {
        const isActive = index === activeView;
        const count = counts[view.key] ?? view.count;
        return (
          <button
            type="button"
            key={view.key || view.label}
            className={`sv-views__tab ${isActive ? 'is-active' : ''}`}
            onClick={() => onChange(index)}
            aria-current={isActive ? 'page' : undefined}
          >
            {view.label}
            {count != null && (
              <span
                className={`sv-views__pill ${view.attention && !isActive ? 'is-attention' : ''} ${
                  isActive ? 'is-active' : ''
                }`}
              >
                {typeof count === 'number' ? count.toLocaleString('en-IN') : count}
              </span>
            )}
          </button>
        );
      })}
      {onSaveView && (
        <button type="button" className="sv-views__save" onClick={onSaveView}>
          ＋ Save current view
        </button>
      )}
    </div>
  );
}

/**
 * Filter bar. `filters` is an array of { key, label, options, placeholder }.
 * The right cluster holds Clear, export and column-picker affordances.
 */
export function FilterBar({
  filters = [],
  values = {},
  onChange,
  query,
  onQueryChange,
  searchPlaceholder = 'Search…',
  onClear,
  showClear,
  onExport,
  onColumns,
  children,
}) {
  return (
    <div className="sv-filters">
      {filters.map((filter) => (
        <Field key={filter.key} label={filter.label} className="sv-filters__item">
          <Select
            options={filter.options || []}
            placeholder={filter.placeholder || 'Any'}
            value={values[filter.key] ?? ''}
            onChange={(e) => onChange(filter.key, e.target.value)}
          />
        </Field>
      ))}

      {onQueryChange && (
        <Field label="Search" className="sv-filters__search">
          <TextInput
            icon="bx-search"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
          />
        </Field>
      )}

      {children}

      <div className="sv-filters__spacer" />

      {showClear && (
        <Button variant="ghost" onClick={onClear}>
          Clear
        </Button>
      )}
      {onExport && <IconButton icon="bx-download" bordered label="Export" onClick={onExport} />}
      {onColumns && <IconButton icon="bx-columns" bordered label="Choose columns" onClick={onColumns} />}
    </div>
  );
}

/** Floating bar shown above the table once at least one row is selected. */
export function BulkActionBar({ count, actions = [], onClear }) {
  if (!count) return null;
  return (
    <div className="sv-bulk">
      <span className="sv-bulk__count">{count} selected</span>
      <span className="sv-bulk__divider" />
      {actions.map((action) => (
        <Button
          key={action.label}
          variant="onDark"
          icon={action.icon}
          onClick={action.onClick}
          disabled={action.disabled}
        >
          {action.label}
        </Button>
      ))}
      <button type="button" className="sv-bulk__clear" onClick={onClear} aria-label="Clear selection">
        <i className="bx bx-x" aria-hidden="true" />
      </button>
    </div>
  );
}

/** "Showing 1–N of M" + pager. */
export function TableFooter({ page, limit, total, totalPages, onPage, noun = 'records' }) {
  if (!total) return null;
  const first = (page - 1) * limit + 1;
  const last = Math.min(page * limit, total);

  const pages = [];
  const window = 2;
  for (let p = 1; p <= totalPages; p += 1) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= window) pages.push(p);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }

  return (
    <div className="sv-table__foot">
      <span className="sv-table__count">
        Showing {first.toLocaleString('en-IN')}–{last.toLocaleString('en-IN')} of{' '}
        {total.toLocaleString('en-IN')} {noun}
      </span>
      <div className="sv-pager">
        <IconButton
          icon="bx-chevron-left"
          size="sm"
          bordered
          label="Previous page"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        />
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className="sv-pager__gap">
              …
            </span>
          ) : (
            <button
              type="button"
              key={p}
              className={`sv-pager__page ${p === page ? 'is-current' : ''}`}
              onClick={() => onPage(p)}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}
        <IconButton
          icon="bx-chevron-right"
          size="sm"
          bordered
          label="Next page"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        />
      </div>
    </div>
  );
}

/**
 * Composes the whole list pattern: view tabs, filter bar, bulk bar, table, footer.
 * Screens supply columns plus a `useListState` result.
 */
export default function ListScreen({
  list,
  columns,
  rowKey,
  filters,
  filterLabels,
  searchPlaceholder,
  bulkActions = [],
  onRowClick,
  onRowAction,
  onExport,
  empty,
  noun = 'records',
  selectable = true,
  searchable = true,
  pickColumns = true,
  onSaveView,
  children,
}) {
  // Which columns are hidden. Seeded from the column definitions so a screen can ship
  // a column that exists but is off until someone asks for it.
  const defaultHidden = useMemo(
    () => columns.filter((c) => c.defaultHidden).map((c) => c.key),
    [columns]
  );
  const [hidden, setHidden] = useState(() => new Set(defaultHidden));
  const visibleColumns = useMemo(
    () => columns.filter((c) => !hidden.has(c.key)),
    [columns, hidden]
  );

  const toggleColumn = (key) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Only render the filter bar when it would hold something. With search off and no
  // dropdown filters it is otherwise an empty band above the table -- and its Clear
  // button would only duplicate what the filter chips already offer.
  const showFilterBar = (filters && filters.length > 0) || searchable || !!onExport;

  return (
    <div className="sv-screen-body">
      {list.views?.length > 0 && (
        <div className="sv-views-row">
          <SavedViewTabs
            views={list.views}
            activeView={list.activeView}
            onChange={list.setActiveView}
            counts={list.counts}
            onSaveView={onSaveView}
          />
          {pickColumns && (
            <ColumnPicker
              columns={columns}
              hidden={hidden}
              onToggle={toggleColumn}
              onReset={() => setHidden(new Set(defaultHidden))}
            />
          )}
        </div>
      )}

      {list.selectedIds.size > 0 ? (
        <BulkActionBar
          count={list.selectedIds.size}
          actions={bulkActions}
          onClear={list.clearSelection}
        />
      ) : (
        showFilterBar && (
          <FilterBar
            filters={filters}
            values={list.filters}
            onChange={list.setFilter}
            query={list.query}
            onQueryChange={searchable ? list.setQuery : undefined}
            searchPlaceholder={searchPlaceholder}
            onClear={list.clearFilters}
            showClear={list.hasActiveFilters}
            onExport={onExport}
          />
        )
      )}

      {list.error && (
        <div className="sv-error-banner" role="alert">
          <i className="bx bx-error-circle" aria-hidden="true" />
          <span>
            Could not load {noun}.
            {/* Say what actually went wrong -- a bare "could not load" gives an admin
                nothing to report and hides whether it was auth, a 404 or a server error. */}
            {(() => {
              const status = list.error?.response?.status;
              const detail = list.error?.response?.data?.error || list.error?.response?.data?.detail;
              if (detail) return ` ${detail}`;
              if (status === 401 || status === 403) return ' Your session may have expired.';
              if (status === 404) return ' That endpoint is not available on this server.';
              if (status) return ` Server returned ${status}.`;
              return ' The server could not be reached.';
            })()}
          </span>
          <Button variant="ghost" onClick={list.reload}>
            Retry
          </Button>
        </div>
      )}

      {children}

      <ActiveFilterChips
        columns={columns}

        columnFilters={list.columnFilters}
        filterLabels={filterLabels}
        onRemove={list.setColumnFilter}
        onClearAll={list.clearColumnFilters}
      />

      <DataTable
        columns={visibleColumns}
        rows={list.items}
        rowKey={rowKey}
        loading={list.loading}
        selectable={selectable}
        selectedIds={list.selectedIds}
        onToggleRow={list.toggleRow}
        onToggleAll={list.toggleAll}
        sort={list.sort}
        onSort={list.toggleSort}
        columnFilters={list.columnFilters}
        onColumnFilter={list.setColumnFilter}
        onRowClick={onRowClick}
        onRowAction={onRowAction}
        empty={empty}
        noun={noun}
        footer={
          <TableFooter
            page={list.page}
            limit={list.limit}
            total={list.total}
            totalPages={list.totalPages}
            onPage={list.setPage}
            noun={noun}
          />
        }
      />
    </div>
  );
}
